import { useEffect, useState, useContext } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import axios from 'axios';
import { usePostHog } from '@posthog/react';

import { ChevronDown, CheckCircle } from "../../files/icons/index.js";

import { EditableDatePicker } from '../components/datePicker.js';
import MaskedInput from "../components/maskedInput.js";

import { AppContext } from '../../context.js';

import Loading from '../components/loading.js';
import Helmet from "../components/helmet.js";

import { Button, Status } from '../components/component.js';

function ReferralCreate() {
  const context = useContext(AppContext);
  const posthog = usePostHog();

  const [searchParams] = useSearchParams();

  // Get templateID from URL params and initialize filter
  //const templateID = searchParams.get('templateID');

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState(null);
  const [partners, setPartners] = useState(null);

  const [selfReferral, setSelfReferral] = useState(true);
  const [partnerReferral, setPartnerReferral] = useState(null); // partner is null or ID of partner

  const [template, setTemplate] = useState(null);

  const [importMode, setImportMode] = useState("single");
  const [selectedFile, setSelectedFile] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  const [form, setForm] = useState({});

  useEffect(() => {
    let u = context.addSpiner();
    axios.all([
      axios.get('/api/admin/settings', {
      }),
      axios.post('/api/admin/partners', {
      })
    ]).then(axios.spread((settings, partners) => {
      setSettings(settings.data);
      setTemplate(settings.data.referralTemplates[0]);
      setPartners(partners.data.partners);

      // Initialize form with default values
      // const newForm = {};
      // newForm["dateOfBirth"] = new Date(0);
      // setForm(newForm);
      setLoading(false);
    })).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, []);

  // Referral source
  function referralSourceChanged(type, partnerID) {
    if (type === "self") {
      setSelfReferral(true);
      setPartnerReferral(null);
    } else {
      setSelfReferral(false);
      setPartnerReferral(partnerID);
    }
  }

  // Template
  function templateChanged(templateID) {
    let selectedTemplate = settings.referralTemplates.find((t) => t.id === templateID);
    setTemplate(selectedTemplate);
  }

  // Referral form

  // Form
  function renderPages() {
    return template.pages.map((page) => {
      return (
        <div key={page.id} className="card mb-2">
          <div className="card-header sm">
            <div className="typo-strong-175">{page.title}</div>
          </div>
          <div className="card-content sm">
            {page.lines.map((line, index) => renderLine(page.id, line, page.lines.length === index + 1))}
          </div>
        </div>
      );
    });
  }

  function renderLine(pageID, line, isLastLine) {
    // Line example
    //[{"code": "firstName", "size": 6}, {"code": "lastName", "size": 6}]

    return (
      <div key={"page_"+pageID+"_line_"+line.id} className={"row " + (isLastLine ? "" : "mb-2")}>
        {line.map((lineField, index) => {
          let field = template.fields.find((f) => f.code === lineField.code);

          return (
            <div key={"page_"+pageID+"_line_"+line.id+"_field_"+field.code} className={`col-xs-${lineField.size} py-0`}>
              <div className="typo-strong-150">{field.title}:</div>

              {field.type === "text" && !field.format && (
                <input type="text" placeholder="" value={form[field.code]} onChange={(e) => changeField(field.code, e.target.value)} className="input sm typo-normal-150 w-100" />
              )}

              {field.type === "text" && field.format && (
                <MaskedInput
                  value={form[field.code]}
                  onChange={(value) => changeField(field.code, value)}
                  formatRegex={field.format}
                  className="input sm typo-normal-150 w-100"
                />
              )}

              {field.type === "date" && (
                <EditableDatePicker 
                  date={form[field.code]}
                  onChange={(e) => changeField(field.code, e)}
                  rightIcon={<ChevronDown width={16} height={16} color={'grey'} />}
                  defaultLevel="year"
                />
              )}

              {field.type === "radio" && (
                field.options.map((option) => {
                  return (
                    <div key={"radio_"+field.code+"_"+option} className={"item typo-normal-150 me-2 " + (form[field.code] == option ? "blue" : "grey")} onClick={(e) => changeField(field.code, option)}>{option}</div>
                  );
                })
              )}

              {/* {field.type === "text" && (
                <input type="text" className="input sm typo-normal-150 w-100" placeholder="" style={{backgroundColor: "#eee"}} value={form[field.code]} onChange={(e) => changeField(field.code, e.target.value)} />
              )}

              {field.type === "radio" && (
                field.options.map((option) => {
                  return (
                    <div key={"radio_"+field.code+"_"+option} className={"item typo-normal-150 me-2 " + (form[field.code] == option ? "blue" : "grey")} onClick={(e) => changeField(field.code, option)}>{option}</div>
                  );
                })
              )} */}
            </div>
          );
        })}
      </div>
    );
  }

  function changeField(name, value) {
    // Create a new object to avoid direct mutation
    const newForm = { ...form, [name]: value ? value : null };
    setForm(newForm);
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Reset the file input so subsequent uploads trigger onChange
      e.target.value = '';
    }
  }

  function downloadTemplate(format) {
    let u = context.addSpiner();
    axios.get(`/api/admin/referrals/template/${template.id}?format=${format}`, {
      responseType: 'blob'
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `referral_template_${template.id}.${format}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function importFile() {
    if (!selectedFile) {
      context.addError(new Error("Please select a file"));
      return;
    }

    let u = context.addSpiner();
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('templateID', template.id);
    formData.append('selfReferral', selfReferral);
    if (partnerReferral) {
      formData.append('partnerID', partnerReferral);
    }

    axios.post('/api/admin/referrals/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then((response) => {
      posthog?.capture('referrals_imported', {
        template_id: template.id,
        self_referral: selfReferral,
      });
      navigate(`/referrals?importID=${response.data.importID}`);
    }).catch((e) => {
      posthog?.capture('referrals_import_failed', {
        template_id: template.id,
        self_referral: selfReferral,
        status: e?.response?.status ?? 0,
        code: e?.response?.data?.code ?? null,
      });
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function create() {
    let u = context.addSpiner();

    axios.post('/api/admin/referrals/create', {
      templateID: template.id,
      selfReferral: selfReferral,
      partnerID: partnerReferral,
      firstName: firstName,
      lastName: lastName,
      email: email,
      phone: phone,
      message: message,
      form: form,
    }).then((response) => {
      posthog?.capture('referral_created', {
        referral_id: response.data.id,
        template_id: template.id,
        self_referral: selfReferral,
        partner_id: partnerReferral,
      });
      navigate('/referral/'+response.data.id);
    }).catch((e) => {
      posthog?.capture('referral_create_failed', {
        template_id: template.id,
        self_referral: selfReferral,
        status: e?.response?.status ?? 0,
        code: e?.response?.data?.code ?? null,
      });
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Helmet title="Referral Create" />

      <div className="container">

        <div className="row mb-4">
          <div className="col-xs-12">
            <span className="typo-hero-small">Referral Create</span>
          </div>
        </div>

        {/* Referral Source */}
        <div className="row">
          <div className="col-xs-12">
            <span className="typo-strong-200">Referral Source</span>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-xs-12">
            
            {/* Self Referral */}
            <div className="row">
              <div className="col-xs-4">
                <div className="card clickable mb-2" onClick={() => referralSourceChanged("self", null)}>
                  <div className="card-header sm">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="w-100">
                        <div className="typo-strong-175">Self Referral</div>
                        <div className="typo-normal-150">This is a self referral form</div>
                      </div>
                      {selfReferral && (
                        <CheckCircle width={24} height={24} color={'grey'} />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Partners */}
              {partners.map((partner) => {
                return (
                  <div key={"partner_"+partner.id} className="col-xs-4">
                    <div className="card clickable mb-2" onClick={() => referralSourceChanged("partner", partner.id)}>
                      <div className="card-header sm">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="w-100">
                            <div className="typo-strong-175">{partner.title}</div>
                            <div className="typo-normal-150">{partner.description}</div>
                          </div>
                          {partnerReferral === partner.id && (
                            <CheckCircle width={24} height={24} color={'grey'} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

            </div>
          </div>
        </div>

        {/* Referral Template */}
        <div className="row">
          <div className="col-xs-12">
            <span className="typo-strong-200">Template</span>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="row">
              {settings.referralTemplates.map((t) => {
                return (
                  <div key={"template_"+t.id} className="col-xs-4">
                    <div className="card clickable mb-2" onClick={() => templateChanged(t.id)}>
                      <div className="card-header sm">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="w-100">
                            <div className="typo-strong-175">{t.title}</div>
                            <div className="typo-normal-150">{t.description}</div>
                          </div>
                          {template.id === t.id && (
                            <CheckCircle width={24} height={24} color={'grey'} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Import Mode Selector */}
        <div className="row">
          <div className="col-xs-12">
            <span className="typo-strong-200">Import Mode</span>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="row">
              <div className="col-xs-4">
                <div className="card clickable mb-2" onClick={() => setImportMode("single")}>
                  <div className="card-header sm">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="w-100">
                        <div className="typo-strong-175">Single Lead</div>
                        <div className="typo-normal-150">Create one referral</div>
                      </div>
                      {importMode === "single" && (
                        <CheckCircle width={24} height={24} color={'grey'} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-4">
                <div className="card clickable mb-2" onClick={() => setImportMode("import")}>
                  <div className="card-header sm">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="w-100">
                        <div className="typo-strong-175">Import File</div>
                        <div className="typo-normal-150">Import multiple referrals</div>
                      </div>
                      {importMode === "import" && (
                        <CheckCircle width={24} height={24} color={'grey'} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Referral Form */}
        {importMode === "single" && (
          <>
            <div className="row">
              <div className="col-xs-12">
                <span className="typo-strong-200">Referral Data</span>
              </div>
            </div>

            <div className="row mb-2">
          
              <div className="col-xs-12">

                <div className="card">
                  <div className="card-header sm">
                    <div className="typo-strong-175">
                      Required Fields
                    </div>
                  </div>
                  <div class="card-content sm">

                    <div class="row mb-2">
                      <div class="col-xs-4">
                        <div class="typo-strong-150">First Name:</div>
                        <input type="text" placeholder="" class="input sm typo-normal-150 w-100" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                      </div>
                      <div class="col-xs-4">
                        <div class="typo-strong-150">Last Name:</div>
                        <input type="text" placeholder="" class="input sm typo-normal-150 w-100" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-xs-4">
                        <div class="typo-strong-150">Email Address:</div>
                        <input type="text" placeholder="" class="input sm typo-normal-150 w-100" value={email} onChange={(e) => setEmail(e.target.value)} />
                        <div class="typo-strong-150 mt-2">Phone Number:</div>
                        <input type="text" placeholder="" class="input sm typo-normal-150 w-100" value={phone} onChange={(e) => setPhone(e.target.value)} />
                      </div>
                      <div class="col-xs-8">
                        <div class="typo-strong-150">Message:</div>
                        <textarea rows="4" placeholder="" class="input sm typo-normal-150 w-100" value={message} onChange={(e) => setMessage(e.target.value)} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-xs-12">
                {renderPages()}
              </div>
            </div>

            <div className="row">
              <div className="col-xs-12 text-end">
              <Button onClick={create} color="blue" text={"Create"} />
              </div>
            </div>
          </>
        )}

        {/* File Import */}
        {importMode === "import" && (
          <>
            <div className="row">
              <div className="col-xs-12">
                <span className="typo-strong-200">Import File</span>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-xs-12">
                <div className="card">
                  <div className="card-header sm">
                    <div className="typo-strong-175">Supported formats: CSV, XLS, JSON</div>
                  </div>
                  <div className="card-content sm">
                    <div className="mb-2">
                      <input type="file" accept=".csv,.xls,.xlsx,.json" onChange={handleFileChange} className="input sm typo-normal-150 w-100" />
                      {selectedFile && (
                        <div className="typo-normal-150 mt-2">Selected: {selectedFile.name}</div>
                      )}
                    </div>
                    <div className="d-flex align-items-center mt-3">
                      <div className="typo-normal-150 me-2">Templates:</div>
                      <div className="item grey typo-normal-150 me-2" onClick={() => downloadTemplate('csv')}>Download CSV</div>
                      <div className="item grey typo-normal-150 me-2" onClick={() => downloadTemplate('xlsx')}>Download XLS</div>
                      <div className="item grey typo-normal-150 me-2" onClick={() => downloadTemplate('json')}>Download JSON</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-xs-12 text-end">
                <Button onClick={importFile} color="blue" text={"Import"} />
              </div>
            </div>
          </>
        )}

      </div>
    </>
  );
}

export default ReferralCreate;
